/**
 * BaseGridLayer Component (BaseGridLayer.tsx)
 * ===========================================
 *
 * This component is responsible for rendering the core visual elements that make up the
 * fretboard's appearance. It creates a realistic representation of a string instrument fretboard
 * using advanced SVG rendering techniques.
 *
 * The component handles the rendering of:
 *
 * 1. Wooden Fretboard Background:
 *    - Trapezoidal wooden background with realistic grain
 *    - Perspective-adjusted shape reflecting the tapered design of real instruments
 *    - SVG gradients and patterns for visual detail
 *
 * 2. Frets:
 *    - Metallic fret wires with proper positioning
 *    - Special treatment for the nut (fret 0)
 *    - Shadows and highlights for depth and realism
 *
 * 3. Strings:
 *    - Variable thickness strings based on musical function
 *    - Different colors and appearances for treble vs. bass strings
 *    - Perspective-adjusted positioning
 *
 * 4. Fret Markers (Inlays):
 *    - Industry-standard marker positions (3, 5, 7, 9, 12, etc.)
 *    - Special double markers at octaves (12th, 24th frets)
 *    - Realistic pearloid or abalone visual appearance
 *
 * This component provides the visual foundation upon which interactive elements
 * like note markers, scales, and chord diagrams can be overlaid.
 */

import { createBaseGridLayer } from '@fretgenius/ui';

import {
  FretboardGrid,
  FretboardStrings,
  FretboardFrets,
  FretboardMarkers,
  FretboardGradients,
} from './components';

/**
 * BaseGridLayer is a React functional component that renders the foundational grid of a fretboard.
 *
 * @remarks
 * This layer renders the static visual elements of the fretboard, including:
 * - A trapezoidal wooden background simulating the fretboard shape.
 * - Horizontal lines representing the strings (with perspective adjustments).
 * - Vertical lines representing the frets, with the nut (0-fret) drawn thicker.
 * - Inlay markers (including double markers at octave positions) positioned between frets.
 *
 * The component is entirely presentational and uses the provided layout dimensions
 * to accurately compute and render all elements.
 *
 * @param {FretboardLayerProps} props - The layout dimensions and fretboard configuration.
 * @returns {JSX.Element} An SVG `<g>` element containing the fretboard grid.
 */
export const BaseGridLayer = createBaseGridLayer({
  G: (props) => <g {...props} />,
  FretboardGradients,
  FretboardGrid,
  FretboardFrets,
  FretboardStrings,
  FretboardMarkers,
});
